import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as React from 'react';
import {
  Container, Typography, Stack, Box, Card, Grid,
  Radio, RadioGroup, FormControlLabel, FormControl,
  TextField, Autocomplete, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Tabs, Tab,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { validateUser, getLightPropostas, getUsuarios, getFornecedores } from './api';
import config from '../config.json';
import HomeEngenhariaServicos from './HomeEngenhariaServicos';
import { cores } from '../styles/styleUtils';
import Iconify from '../components/iconify';

// ── Cores das etapas ───────────────────────────────────────────────────
const CORES_ETAPAS = [
  '#1565C0', '#1976D2', '#1E88E5', '#42A5F5',
  '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD',
];

// Mapeamento: nome exibido → nomes reais no banco (on-grid e bombeamento)
// fluxos: null = qualquer fluxo, [4,5] = já enviado para montagem
const ETAPAS_ENGENHARIA_MAP = [
  { label: 'Análise Contrato',              nomes: ['Análise Contrato'],                        fluxos: [3] },
  { label: 'Compra do Kit',                 nomes: ['Compra do Kit'],                            fluxos: [3] },
  { label: 'Confecção Projeto',             nomes: ['Confecção Projeto'],                        fluxos: [3] },
  { label: 'Emissão ART',                   nomes: ['Emissão ART'],                              fluxos: [3] },
  { label: 'Protocolo / Projeto Concluído', nomes: ['Protocolo', 'Projeto Concluído'],           fluxos: [3] },
  { label: 'Parecer / Envio Montagem',      nomes: ['Parecer de Acesso', 'Envio Montagem'],      fluxos: [3] },
  { label: 'Projeto de Implantação',        nomes: ['Projeto de Implantação'],                   fluxos: [3] },
  { label: 'Enviado para Montagem',         nomes: null,                                         fluxos: [4, 5, 6] },
];
const ETAPAS_ENGENHARIA = ETAPAS_ENGENHARIA_MAP.map(e => e.label);

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── Card de KPI ────────────────────────────────────────────────────────
function KpiCard({ titulo, valor, icon, sub }) {
  return (
    <Card sx={{
      p: 2, minWidth: 150, flex: 1, textAlign: 'center',
      background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
      borderTop: `4px solid ${cores.laranja}`,
      borderRadius: 2, boxShadow: 3,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
    }}>
      {icon && <Iconify icon={icon} sx={{ color: cores.laranja, width: 22, height: 22 }} />}
      <Typography sx={{ color: '#bdbdbd', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {titulo}
      </Typography>
      <Typography sx={{ color: cores.textoClaro, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
        {valor}
      </Typography>
      {sub && <Typography sx={{ color: '#9e9e9e', fontSize: 11 }}>{sub}</Typography>}
    </Card>
  );
}

// ── Filtro de período ─────────────────────────────────────────────────
function FiltroPeriodo({ filtro, setFiltro, usuarios, fornecedores, isSupervisor }) {
  return (
    <Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
      <TextField
        size="small" type="date" label="Data Inicial"
        value={filtro.dataInicial} InputLabelProps={{ shrink: true }}
        onChange={(e) => setFiltro((f) => ({ ...f, dataInicial: e.target.value }))}
        sx={{ backgroundColor: 'white', minWidth: 160 }}
      />
      <TextField
        size="small" type="date" label="Data Final"
        value={filtro.dataFinal} InputLabelProps={{ shrink: true }}
        onChange={(e) => setFiltro((f) => ({ ...f, dataFinal: e.target.value }))}
        sx={{ backgroundColor: 'white', minWidth: 160 }}
      />
      {isSupervisor && (
        <Autocomplete
          size="small" options={usuarios} sx={{ minWidth: 200, backgroundColor: 'white' }}
          getOptionLabel={(o) => o.nome} value={filtro.engenheiro || null}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onChange={(_, v) => setFiltro((f) => ({ ...f, engenheiro: v }))}
          renderInput={(p) => <TextField {...p} label="Engenheiro" />}
        />
      )}
      <Autocomplete
        size="small" options={fornecedores} sx={{ minWidth: 200, backgroundColor: 'white' }}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.nome || '')}
        value={filtro.fornecedor || null}
        isOptionEqualToValue={(o, v) => (o._id && v._id ? o._id === v._id : o === v)}
        onChange={(_, v) => setFiltro((f) => ({ ...f, fornecedor: v }))}
        renderInput={(p) => <TextField {...p} label="Fornecedor" />}
      />
    </Stack>
  );
}

// ── Componente principal ───────────────────────────────────────────────
export default function HomeEngenharia() {
  const navigate = useNavigate();
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
  const isSupervisor = usuarioLogado?.supervisor;

  const [propostas, setPropostas] = useState([]);
  const [propostasServico, setPropostasServico] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [radioFunil, setRadioFunil] = useState('quantidade');
  const [aba, setAba] = useState('produtos');

  // Filtro global
  const dataAtual = new Date();
  const primeiroDia = new Date(dataAtual.getFullYear(), 0, 1)
    .toISOString().split('T')[0];
  const ultimoDia = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0)
    .toISOString().split('T')[0];

  const [filtro, setFiltro] = useState({
    dataInicial: primeiroDia,
    dataFinal: ultimoDia,
    engenheiro: null,
    fornecedor: null,
  });

  // Filtros individuais por gráfico
  const [filtroFunil, setFiltroFunil] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null, fornecedor: null });
  const [filtroDistrib, setFiltroDistrib] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null, fornecedor: null });
  const [filtroFornec, setFiltroFornec] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null, fornecedor: null });
  const [filtroSemanas, setFiltroSemanas] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null, fornecedor: null });
  const [filtroReceita, setFiltroReceita] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null, fornecedor: null });

  useEffect(() => {
    const validateuser = async () => {
      const teste = await validateUser();
      if (!teste.ok) {
        localStorage.removeItem('userData');
        localStorage.removeItem('userToken');
        config.user = {};
        navigate('/', { replace: true });
        return;
      }
      localStorage.setItem('userData', JSON.stringify(teste.usuario));
      config.user = teste.usuario;
    };
    validateuser();
  }, []);

  useEffect(() => {
    const fetchAux = async () => {
      const [u, f] = await Promise.all([getUsuarios(), getFornecedores()]);
      setUsuarios(u || []);
      setFornecedores(f || []);
    };
    fetchAux();
  }, []);

  useEffect(() => {
    const fetchPropostas = async () => {
      const filtroApi = {
        engenharia: true,
        datainicial: filtro.dataInicial,
        datafinal: filtro.dataFinal,
        usuarioLogado,
        ...(filtro.engenheiro && { responsavel: filtro.engenheiro }),
        ...(filtro.fornecedor && { fornecedor: filtro.fornecedor }),
      };
      const result = await getLightPropostas(filtroApi);
      setPropostas(result || []);

      // Serviços em Engenharia (fluxo próprio) — busca separada, para não poluir a agregação de produtos
      const resultServ = await getLightPropostas({
        servicos: true,
        datainicial: filtro.dataInicial,
        datafinal: filtro.dataFinal,
        usuarioLogado,
        ...(filtro.engenheiro && { responsavel: filtro.engenheiro }),
      });
      setPropostasServico(resultServ || []);
    };
    fetchPropostas();
  }, [filtro]);

  // Filtrar propostas localmente para cada gráfico
  const filtrarLocal = (lista, f) => lista.filter((p) => {
    const d = new Date(p.data);
    const di = f.dataInicial ? new Date(f.dataInicial) : null;
    const df = f.dataFinal ? new Date(`${f.dataFinal}T23:59:59`) : null;
    if (di && d < di) return false;
    if (df && d > df) return false;
    if (f.engenheiro && p.responsavel?._id !== f.engenheiro._id) return false;
    if (f.fornecedor && p.fornecedor?._id !== f.fornecedor._id) return false;
    return true;
  });

  const propostasFunil   = useMemo(() => filtrarLocal(propostas, filtroFunil),   [propostas, filtroFunil]);
  const propostasDistrib = useMemo(() => filtrarLocal(propostas, filtroDistrib), [propostas, filtroDistrib]);
  const propostasFornec  = useMemo(() => filtrarLocal(propostas, filtroFornec),  [propostas, filtroFornec]);
  const propostasSemanas = useMemo(() => filtrarLocal(propostas, filtroSemanas), [propostas, filtroSemanas]);
  const propostasReceita = useMemo(() => filtrarLocal(propostas, filtroReceita), [propostas, filtroReceita]);

  // ── Dados calculados ─────────────────────────────────────────────────

  // Cards KPI
  const totalProjetos = propostas.length;
  const totalKwp = propostas.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0);
  const totalReceita = propostas.reduce((s, p) => s + (p.valorservico || p.valorprojeto || 0), 0);
  const ETAPAS_ATIVAS = ['Confecção Projeto','Emissão ART','Protocolo','Parecer de Acesso','Projeto de Implantação'];
  const projetosAtivos = propostas.filter((p) =>
    (p.etapa?.id >= 2) || ETAPAS_ATIVAS.includes(p.etapa?.nome)
  ).length;

  // Funil por etapa
  const dadosFunil = useMemo(() => ETAPAS_ENGENHARIA_MAP.map((etapaMap, i) => {
    const propsEtapa = propostasFunil.filter((p) => {
      // Etapa "Enviado para Montagem" — fluxo 4, 5 ou 6
      if (etapaMap.nomes === null) return etapaMap.fluxos.includes(p.fluxo?.id);
      // Demais etapas — verifica nome E fluxo
      return etapaMap.fluxos.includes(p.fluxo?.id) && etapaMap.nomes.includes(p.etapa?.nome);
    });
    return {
      name: etapaMap.label,
      quantidade: propsEtapa.length,
      kwp: parseFloat(propsEtapa.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0).toFixed(2)),
      fill: CORES_ETAPAS[i % CORES_ETAPAS.length],
    };
  }), [propostasFunil]);

  // Funil por produto
  const PRODUTOS_ENGENHARIA = ['Gerador On-Grid', 'Gerador Grid-Zero', 'Gerador Off-Grid', 'Sistema de Bombeamento'];
  const CORES_PRODUTOS = ['#1565C0', '#2e7d32', '#6a1b9a', '#e65100'];

  const dadosFunilProduto = useMemo(() => {
    const mapa = {};
    propostasFunil.forEach((p) => {
      const prod = p.produto?.nome || 'Outros';
      if (!mapa[prod]) mapa[prod] = { quantidade: 0, kwp: 0, receita: 0 };
      mapa[prod].quantidade += 1;
      mapa[prod].kwp += p.kit?.potenciacc || 0;
      mapa[prod].receita += p.valorservico || p.valorprojeto || 0;
    });
    return Object.entries(mapa)
      .map(([nome, v], i) => ({
        name: nome,
        quantidade: v.quantidade,
        kwp: parseFloat(v.kwp.toFixed(2)),
        receita: parseFloat(v.receita.toFixed(2)),
        fill: CORES_PRODUTOS[PRODUTOS_ENGENHARIA.indexOf(nome)] || '#90a4ae',
      }))
      .sort((a, b) => b.quantidade - a.quantidade);
  }, [propostasFunil]);

  // Ticket médio por potência (a partir de Confecção Projeto — etapa id >= 2)
  const projetosHomologados = propostas.filter((p) =>
    (p.etapa?.id >= 2) ||
    ETAPAS_ATIVAS.includes(p.etapa?.nome) ||
    [4, 5, 6].includes(p.fluxo?.id)
  );
  const ticketMedio = projetosHomologados.length > 0
    ? (projetosHomologados.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0) / projetosHomologados.length).toFixed(2)
    : 0;

  const faixasPotencia = useMemo(() => {
    const faixas = {
      '0 a 7,5 kWp':     [],
      '7,6 a 20,0 kWp':  [],
      '20,1 a 45,0 kWp': [],
      '45,1 a 75,0 kWp': [],
      'Acima de 75 kWp':  [],
    };
    projetosHomologados.forEach((p) => {
      const kw = p.kit?.potenciacc || 0;
      if (kw <= 7.5)       faixas['0 a 7,5 kWp'].push(p);
      else if (kw <= 20.0) faixas['7,6 a 20,0 kWp'].push(p);
      else if (kw <= 45.0) faixas['20,1 a 45,0 kWp'].push(p);
      else if (kw <= 75.0) faixas['45,1 a 75,0 kWp'].push(p);
      else                 faixas['Acima de 75 kWp'].push(p);
    });
    return Object.entries(faixas).map(([faixa, ps]) => ({
      faixa,
      quantidade: ps.length,
      potenciaTotal: parseFloat(ps.reduce((s, p) => s + (p.kit?.potenciacc || 0), 0).toFixed(2)),
    }));
  }, [projetosHomologados]);

  // Top Distribuidoras
  const dadosDistribuidoras = useMemo(() => {
    const mapa = {};
    propostasDistrib.forEach((p) => {
      const isBomb   = p.produto?.nome === 'Sistema de Bombeamento';
      const isOffGrid = p.produto?.nome === 'Gerador Off-Grid';
      const nome = (isBomb || isOffGrid)
        ? (isBomb ? 'Bombeamento (sem rede)' : 'Off-Grid (sem rede)')
        : (typeof p.distribuidora === 'string' ? p.distribuidora : p.distribuidora?.nome) || 'Não informada';
      mapa[nome] = (mapa[nome] || 0) + 1;
    });
    return Object.entries(mapa)
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 8);
  }, [propostasDistrib]);

  // Top Fornecedores
  const dadosFornecedores = useMemo(() => {
    const mapa = {};
    propostasFornec.forEach((p) => {
      const nome = p.kit?.fornecedor?.nome || p.fornecedor?.nome || 'Não informado';
      mapa[nome] = (mapa[nome] || 0) + 1;
    });
    return Object.entries(mapa)
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 8);
  }, [propostasFornec]);

  // Projetos por semana do mês corrente
  const dadosSemanas = useMemo(() => {
    const semanas = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'];
    const contagem = [0, 0, 0, 0];
    const hoje = new Date();
    propostasSemanas.forEach((p) => {
      const d = new Date(p.data);
      if (d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()) {
        const dia = d.getDate();
        if (dia <= 7) contagem[0] += 1;
        else if (dia <= 14) contagem[1] += 1;
        else if (dia <= 21) contagem[2] += 1;
        else contagem[3] += 1;
      }
    });
    return semanas.map((s, i) => ({ semana: s, quantidade: contagem[i] }));
  }, [propostasSemanas]);

  // Receita por mês
  const dadosReceita = useMemo(() => {
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const receitas = Array(12).fill(0);
    propostasReceita.forEach((p) => {
      // On-grid usa valorservico; bombeamento usa valorprojeto
      const receita = p.valorservico || p.valorprojeto || 0;
      if (receita) {
        const mes = new Date(p.data).getMonth();
        receitas[mes] += receita;
      }
    });
    return meses.map((mes, i) => ({ mes, receita: parseFloat(receitas[i].toFixed(2)) }));
  }, [propostasReceita]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet><title>Home — Engenharia</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#1565C0" mb={3}>
          Dashboard Engenharia
        </Typography>

        <Tabs value={aba} onChange={(e, v) => setAba(v)} sx={{ mb: 3 }}>
          <Tab label="Produtos" value="produtos" />
          <Tab label="Serviços" value="servicos" />
        </Tabs>

        {aba === 'produtos' && (
        <>
        {/* ── Cards KPI ── */}
        <Stack direction="row" gap={2} flexWrap="wrap" mb={4}>
          <KpiCard titulo="Total de Projetos" valor={fmtNum(totalProjetos)} icon="eva:clipboard-outline" />
          <KpiCard titulo="Potência Total" valor={`${totalKwp.toFixed(2)} kWp`} icon="game-icons:solar-power" />
          <KpiCard titulo="Projetos em Andamento" valor={fmtNum(projetosAtivos)} icon="eva:activity-outline" />
          <KpiCard titulo="Ticket Médio (kWp)" valor={`${ticketMedio} kWp`} icon="eva:flash-outline" />
          <KpiCard titulo="Receita Serviços" valor={fmtBRL(totalReceita)} icon="eva:trending-up-outline" />
        </Stack>

        {/* ── Funil por Produto ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>
            Projetos por Produto
          </Typography>
          <FiltroPeriodo filtro={filtroFunil} setFiltro={setFiltroFunil} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dadosFunilProduto} layout="vertical" margin={{ left: 20, right: 50 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [`${v} projetos`, 'Quantidade']} />
              <Bar dataKey="quantidade" radius={[0, 4, 4, 0]}>
                {dadosFunilProduto.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
                <LabelList dataKey="quantidade" position="right" style={{ fontSize: 12, fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Funil de Etapas ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight="bold" color="#1565C0">
              Funil de Projetos por Etapa
            </Typography>
            <FormControl>
              <RadioGroup row value={radioFunil} onChange={(e) => setRadioFunil(e.target.value)}>
                <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Quantidade" />
                <FormControlLabel value="kwp" control={<Radio size="small" />} label="kWp" />
              </RadioGroup>
            </FormControl>
          </Stack>
          <FiltroPeriodo filtro={filtroFunil} setFiltro={setFiltroFunil} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dadosFunil} layout="vertical" margin={{ left: 140 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey={radioFunil} radius={[0, 4, 4, 0]}>
                {dadosFunil.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Ticket Médio por Faixa ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>
            Ticket Médio por Faixa de Potência
            <Typography component="span" variant="caption" color="text.secondary" ml={1}>
              (projetos a partir de Confecção Projeto)
            </Typography>
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#E3F2FD' }}>
                  <TableCell><strong>Faixa de Potência</strong></TableCell>
                  <TableCell align="center"><strong>Quantidade</strong></TableCell>
                  <TableCell align="center"><strong>Potência Total (kWp)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faixasPotencia.map((row) => (
                  <TableRow key={row.faixa} sx={{ '&:hover': { backgroundColor: '#E3F2FD' } }}>
                    <TableCell><Chip label={row.faixa} size="small" color="primary" variant="outlined" /></TableCell>
                    <TableCell align="center">{row.quantidade}</TableCell>
                    <TableCell align="center">{row.potenciaTotal} kWp</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#E3F2FD' }}>
                  <TableCell><strong>Ticket Médio Global</strong></TableCell>
                  <TableCell align="center"><strong>{projetosHomologados.length}</strong></TableCell>
                  <TableCell align="center"><strong>{ticketMedio} kWp</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── Top Distribuidoras ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={1}>
            Top Distribuidoras
          </Typography>
          <FiltroPeriodo filtro={filtroDistrib} setFiltro={setFiltroDistrib} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dadosDistribuidoras} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#1565C0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Top Fornecedores ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#2e7d32" mb={1}>
            Top Fornecedores
          </Typography>
          <FiltroPeriodo filtro={filtroFornec} setFiltro={setFiltroFornec} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dadosFornecedores} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#2e7d32" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Projetos por Semana ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#e65100" mb={1}>
            Projetos Encaminhados — Semanas do Mês Atual
          </Typography>
          <FiltroPeriodo filtro={filtroSemanas} setFiltro={setFiltroSemanas} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosSemanas} margin={{ top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="semana" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#e65100" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Receita por Mês ── */}
        <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#6a1b9a" mb={1}>
            Receita de Serviços por Mês
          </Typography>
          <FiltroPeriodo filtro={filtroReceita} setFiltro={setFiltroReceita} usuarios={usuarios} fornecedores={fornecedores} isSupervisor={isSupervisor} />
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dadosReceita} margin={{ top: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtBRL(v)} />
              <Bar dataKey="receita" fill="#6a1b9a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        </>
        )}

        {aba === 'servicos' && (
          <HomeEngenhariaServicos propostas={propostasServico} usuarios={usuarios} isSupervisor={isSupervisor} />
        )}

      </Container>
    </>
  );
}